import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { useSelector } from "react-redux";
import MapTooltip from "./MapTooltip";
import ClusterListItem from "./ClusterListItem";
import "./index.scss";

const containerStyle = {
  width: "100%",
  height: "80vh",
};

const defaultCenter = {
  lat: 42.876, // Bishkek default
  lng: 74.607,
};

// Get project position from contract_location coordinates
const getProjectPosition = (project) => {
  const lat = project?.contract_address_latitude;
  const lng = project?.contract_address_longitude;

  if (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng)
  ) {
    return { lat, lng };
  }

  return null;
};

// Check if project is active
const isProjectActive = (project) => {
  return project?.inactive !== 1 && !project?.completed;
};

// Create custom marker icon with count
const createClusterIcon = (count, hasActive = true) => {
  const size = count > 99 ? 56 : 48;
  const fontSize = count > 99 ? 14 : 16;
  const backgroundColor = hasActive ? "#2563eb" : "#6b7280"; // Blue for active, gray for inactive

  const iconConfig = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
            <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
                <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${backgroundColor}" stroke="#fff" stroke-width="2"/>
                <text x="${size / 2}" y="${size / 2 + 2}" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="bold" fill="#fff" text-anchor="middle" dominant-baseline="middle">${count}</text>
            </svg>
        `)}`,
  };

  if (
    window.google &&
    window.google.maps &&
    window.google.maps.Size &&
    window.google.maps.Point
  ) {
    iconConfig.scaledSize = new window.google.maps.Size(size, size);
    iconConfig.anchor = new window.google.maps.Point(size / 2, size / 2);
  }

  return iconConfig;
};

// Create custom marker icon for individual projects
const createProjectIcon = (isActive) => {
  const size = 44;
  const color = isActive ? "#2563eb" : "#6b7280"; // Blue for active, gray for inactive

  const iconConfig = {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
             <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.99987 1.33334C5.25987 1.33334 3.0332 3.56001 3.0332 6.30001C3.0332 9.56001 7.1532 14.1733 7.32654 14.3667C7.49987 14.56 7.74653 14.6667 7.99987 14.6667C8.2532 14.6667 8.49986 14.56 8.6732 14.3667C9.1132 13.88 12.9665 9.48668 12.9665 6.30001C12.9665 3.56001 10.7399 1.33334 7.99987 1.33334ZM7.99987 13.62C7.45987 12.9933 4.0332 8.96667 4.0332 6.30001C4.0332 4.11334 5.8132 2.33334 7.99987 2.33334C10.1865 2.33334 11.9665 4.11334 11.9665 6.30001C11.9665 8.55334 9.4132 12.02 7.99987 13.62Z" fill="red"/>
                  <path d="M7.99987 1.33334C5.25987 1.33334 3.0332 3.56001 3.0332 6.30001C3.0332 9.56001 7.1532 14.1733 7.32654 14.3667C7.49987 14.56 7.74653 14.6667 7.99987 14.6667C8.2532 14.6667 8.49986 14.56 8.6732 14.3667C9.1132 13.88 12.9665 9.48668 12.9665 6.30001C12.9665 3.56001 10.7399 1.33334 7.99987 1.33334ZM5.91987 6.30001C5.91987 5.15334 6.8532 4.22001 7.99987 4.22001C9.14653 4.22001 10.0799 5.15334 10.0799 6.30001C10.0799 7.44667 9.14653 8.38002 7.99987 8.38002C6.8532 8.38002 5.91987 7.44667 5.91987 6.30001Z" fill="${color}"/>
             </svg>
        `)}`,
  };

  if (
    window.google &&
    window.google.maps &&
    window.google.maps.Size &&
    window.google.maps.Point
  ) {
    iconConfig.scaledSize = new window.google.maps.Size(size, size);
    iconConfig.anchor = new window.google.maps.Point(size / 2, size);
  }

  return iconConfig;
};

export default function GoogleMapComponent({
  projects = [],
  translations,
  search,
  setShowDeleteModal,
  setDeletingProject,
}) {
  const user = useSelector((state) => state?.adbReducer?.user);

  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [mapZoom, setMapZoom] = useState(5);
  const [mapInstance, setMapInstance] = useState(null);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const [showAllClusterProjects, setShowAllClusterProjects] = useState(false);
  const clusterListRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [mapCenter, setMapCenter] = useState(null);
  const isMapInitialized = useRef(false);
  const initialCenterRef = useRef(null);
  const [infoWindowPosition, setInfoWindowPosition] = useState(null);

  // Check if Google Maps API is loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (window.google?.maps) {
        setIsGoogleMapsReady(true);
      }
    };

    // Check immediately
    checkGoogleMaps();

    // Also check periodically in case it loads after component mounts
    const interval = setInterval(() => {
      if (window.google?.maps && !isGoogleMapsReady) {
        setIsGoogleMapsReady(true);
        clearInterval(interval);
      }
    }, 100);

    // Cleanup after 5 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isGoogleMapsReady]);

  // Group projects by location (similar coordinates)
  const locationGroups = useMemo(() => {
    if (!projects?.length) return {};

    const groups = {};
    const threshold = 0.001; // ~100m threshold for grouping

    projects.forEach((project) => {
      const basePosition = getProjectPosition(project);
      if (!basePosition) return;

      // Find existing group within threshold
      let foundGroup = null;
      for (const key in groups) {
        const [lat, lng] = key.split("_").map(Number);
        const distance = Math.sqrt(
          Math.pow(basePosition.lat - lat, 2) +
            Math.pow(basePosition.lng - lng, 2),
        );
        if (distance < threshold) {
          foundGroup = key;
          break;
        }
      }

      if (foundGroup) {
        groups[foundGroup].push({
          id: project?.id,
          basePosition,
          project,
        });
      } else {
        const key = `${basePosition.lat.toFixed(6)}_${basePosition.lng.toFixed(6)}`;
        groups[key] = [
          {
            id: project?.id,
            basePosition,
            project,
          },
        ];
      }
    });

    return groups;
  }, [projects]);

  // Determine if we should show clusters or individual markers based on zoom
  const shouldCluster = mapZoom < 12; // Cluster when zoom < 12

  // Build markers: clusters when zoomed out, individual when zoomed in
  const markers = useMemo(() => {
    if (!projects?.length || !isGoogleMapsReady || !window.google?.maps)
      return [];

    const result = [];

    Object.values(locationGroups).forEach((group) => {
      if (shouldCluster && group.length > 1) {
        // Show cluster marker
        const hasActive = group.some((item) => isProjectActive(item.project));
        const centerLat =
          group.reduce((sum, item) => sum + item.basePosition.lat, 0) /
          group.length;
        const centerLng =
          group.reduce((sum, item) => sum + item.basePosition.lng, 0) /
          group.length;

        result.push({
          id: `cluster_${group[0].basePosition.lat}_${group[0].basePosition.lng}`,
          position: { lat: centerLat, lng: centerLng },
          isCluster: true,
          count: group.length,
          projects: group.map((item) => item.project),
          hasActive,
          icon: createClusterIcon(group.length, hasActive),
        });
      } else {
        // Check if all projects have exactly the same lat/lon
        const allSamePosition = group.every(
          (item) =>
            item.basePosition.lat === group[0].basePosition.lat &&
            item.basePosition.lng === group[0].basePosition.lng,
        );

        if (allSamePosition) {
          // Place all markers exactly on top of each other
          group.forEach((item) => {
            result.push({
              id: item.id,
              position: item.basePosition,
              project: item.project,
              isCluster: false,
              icon: createProjectIcon(isProjectActive(item.project)),
            });
          });
        } else {
          // Optional: slightly offset markers if very close but not exactly same
          const offsetDistance = 0.0001; // ~70–80m
          group.forEach((item, index) => {
            const angle = (2 * Math.PI * index) / group.length;
            const latOffset = offsetDistance * Math.cos(angle);
            const lngOffset = offsetDistance * Math.sin(angle);

            result.push({
              id: item.id,
              position: {
                lat: item.basePosition.lat + latOffset,
                lng: item.basePosition.lng + lngOffset,
              },
              project: item.project,
              isCluster: false,
              icon: createProjectIcon(isProjectActive(item.project)),
            });
          });
        }
      }
    });

    return result;
  }, [locationGroups, shouldCluster, isGoogleMapsReady]);

  // // Calculate initial center only once when markers are first available
  // useEffect(() => {
  //     if (!isMapInitialized.current && markers?.length > 0 && !initialCenterRef.current) {
  //         const initialCenter = markers[0].position;
  //         initialCenterRef.current = initialCenter;
  //         setMapCenter(initialCenter);
  //         // If map is already loaded, set center programmatically
  //         if (mapInstance) {
  //             mapInstance.setCenter(initialCenter);
  //             isMapInitialized.current = true;
  //         }
  //     }
  // }, [markers, mapInstance]);
  useEffect(() => {
    // console.log('mnjnkj',markers, mapInstance?.getZoom())
    if (!mapInstance || !markers.length) return;

    // ONE marker
    if (markers.length === 1) {
      mapInstance.setCenter(markers[0].position);
      const zoom = mapInstance.getZoom();
      if (zoom > 13) {
        mapInstance.setZoom(13);
      } else mapInstance.setZoom(13);
      return;
    }

    // MULTIPLE markers → fit bounds
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker.position));
    mapInstance.fitBounds(bounds);

    // Prevent crazy zoom
    // setTimeout(() => {
    //     const zoom = mapInstance.getZoom();
    //     if (zoom > 13) {
    //         mapInstance.setZoom(13);
    //     }
    // }, 150);
  }, [markers]);

  useEffect(() => {
    // Projects changed (search, filter, etc.)
    isMapInitialized.current = false;
    setSelectedProjectId(null);
  }, [projects]);

  // Use stable initial center - memoized to prevent unnecessary recalculations
  const center = useMemo(() => {
    return initialCenterRef.current || mapCenter || defaultCenter;
  }, [mapCenter]);

  const selectedMarker = useMemo(() => {
    if (!selectedProjectId) return null;

    // Convert to string for comparison
    const selectedId = String(selectedProjectId);

    // Check if it's a cluster marker
    if (selectedId.startsWith("cluster_")) {
      return (
        markers?.find((marker) => String(marker.id) === selectedId) || null
      );
    }

    // Find individual marker
    return markers?.find((marker) => String(marker.id) === selectedId) || null;
  }, [markers, selectedProjectId]);

  // Find selected project - either from marker or directly from projects array
  const selectedProject = useMemo(() => {
    if (selectedMarker?.project) {
      return selectedMarker.project;
    }
    // If marker not found (e.g., when clustered), find project directly
    if (selectedProjectId) {
      const projectId = String(selectedProjectId);
      return projects?.find((p) => String(p?.id) === projectId) || null;
    }
    return null;
  }, [selectedMarker, selectedProjectId, projects]);

  const selectedCluster = selectedMarker?.isCluster ? selectedMarker : null;

  // Reset cluster projects view when selected marker changes
  useEffect(() => {
    setShowAllClusterProjects(false);
  }, [selectedProjectId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);
  const handleZoomChanged = useCallback(() => {
    if (mapInstance) {
      const zoom = mapInstance.getZoom();
      setMapZoom(zoom);
    }
  }, [mapInstance, markers, search]);

  return (
    <>
      <LoadScript googleMapsApiKey={"AIzaSyDthSzSl8bPrdEc0Mmb9RrVH62prk1trbc"}>
        {isGoogleMapsReady && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter || defaultCenter}
            zoom={mapZoom || 5}
            onLoad={(map) => {
              setMapInstance(map);

              // Fit bounds if multiple markers
              if (markers.length > 1) {
                const bounds = new window.google.maps.LatLngBounds();
                markers.forEach((m) => bounds.extend(m.position));
                map.fitBounds(bounds);
              } else if (markers.length === 1) {
                map.setCenter(markers[0].position);
                map.setZoom(13);
              }
            }}
            // onZoomChanged={() => {
            //     console.log('onZoomChanged')
            //     if (mapInstance) setMapZoom(mapInstance.getZoom());
            // }}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            {markers?.map((marker) => (
              <Marker
                key={marker.id}
                position={marker.position}
                icon={marker.icon}
                onMouseOver={() => {
                  if (closeTimeoutRef.current) {
                    clearTimeout(closeTimeoutRef.current);
                    closeTimeoutRef.current = null;
                  }
                  setSelectedProjectId(marker.id);
                }}
                onMouseOut={() => {
                  closeTimeoutRef.current = setTimeout(() => {
                    setSelectedProjectId(null);
                  }, 300);
                }}
              />
            ))}

            {selectedCluster && selectedMarker && (
              <InfoWindow
                position={selectedMarker.position}
                maxWidth={700}
                onDomReady={() => {
                  // Attach mouse event listeners to InfoWindow container to prevent blinking
                  setTimeout(() => {
                    const infoWindowContainers =
                      document.querySelectorAll(".gm-style-iw-d");
                    const lastContainer =
                      infoWindowContainers[infoWindowContainers.length - 1];
                    if (lastContainer) {
                      const handleMouseOver = () => {
                        if (closeTimeoutRef.current) {
                          clearTimeout(closeTimeoutRef.current);
                          closeTimeoutRef.current = null;
                        }
                      };
                      const handleMouseOut = () => {
                        closeTimeoutRef.current = setTimeout(() => {
                          setSelectedProjectId(null);
                        }, 300);
                      };
                      lastContainer.addEventListener(
                        "mouseover",
                        handleMouseOver,
                      );
                      lastContainer.addEventListener(
                        "mouseout",
                        handleMouseOut,
                      );

                      // Hide close button
                      const closeButton =
                        lastContainer.parentElement?.querySelector(
                          ".gm-ui-hover-effect",
                        );
                      if (closeButton) {
                        closeButton.style.display = "none";
                      }
                    }

                    // Attach mouse event listeners to InfoWindow tail/arrow element
                    const infoWindowTails =
                      document.querySelectorAll(".gm-style-iw-tc");
                    const lastTail =
                      infoWindowTails[infoWindowTails.length - 1];
                    if (lastTail) {
                      const handleMouseOver = () => {
                        if (closeTimeoutRef.current) {
                          clearTimeout(closeTimeoutRef.current);
                          closeTimeoutRef.current = null;
                        }
                      };
                      const handleMouseOut = () => {
                        closeTimeoutRef.current = setTimeout(() => {
                          setSelectedProjectId(null);
                        }, 300);
                      };
                      lastTail.addEventListener("mouseover", handleMouseOver);
                      lastTail.addEventListener("mouseout", handleMouseOut);
                    }
                  }, 0);
                }}
              >
                <div className="map-cluster-info">
                  <h3 className="map-cluster-info__title">
                    {selectedMarker.count}{" "}
                    {selectedMarker.count === 1 ? "Project" : "Projects"}
                  </h3>
                  <div className="map-cluster-info__list" ref={clusterListRef}>
                    {(showAllClusterProjects
                      ? selectedMarker.projects
                      : selectedMarker.projects?.slice(0, 5)
                    )?.map((project) => (
                      <ClusterListItem
                        key={project?.id}
                        project={project}
                        mapInstance={mapInstance}
                        setSelectedProjectId={setSelectedProjectId}
                        setInfoWindowPosition={setInfoWindowPosition}
                        getProjectPosition={getProjectPosition}
                        setImagePreview={setImagePreview}
                        translations={translations}
                        user={user}
                        setDeletingProject={setDeletingProject}
                        setShowDeleteModal={setShowDeleteModal}
                        isInsideCluster
                      />
                    ))}
                    {selectedMarker.projects?.length > 5 &&
                      !showAllClusterProjects && (
                        <button
                          type="button"
                          className="map-cluster-info__more"
                          onClick={() => {
                            setShowAllClusterProjects(true);
                            if (clusterListRef.current) {
                              clusterListRef.current.scrollTop = 0;
                            }
                          }}
                        >
                          +{selectedMarker.projects.length - 5} more projects
                        </button>
                      )}
                  </div>
                </div>
              </InfoWindow>
            )}

            {selectedProject &&
              (!selectedMarker || !selectedMarker.isCluster) && (
                <InfoWindow
                  maxWidth={650}
                  position={
                    infoWindowPosition ||
                    selectedMarker?.position ||
                    getProjectPosition(selectedProject) ||
                    defaultCenter
                  }
                  onDomReady={() => {
                    // Attach mouse event listeners to InfoWindow container to prevent blinking
                    setTimeout(() => {
                      const infoWindowContainers =
                        document.querySelectorAll(".gm-style-iw-d");
                      const lastContainer =
                        infoWindowContainers[infoWindowContainers.length - 1];
                      if (lastContainer) {
                        const handleMouseOver = () => {
                          if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                            closeTimeoutRef.current = null;
                          }
                        };
                        const handleMouseOut = () => {
                          closeTimeoutRef.current = setTimeout(() => {
                            setSelectedProjectId(null);
                            setInfoWindowPosition(null);
                          }, 300);
                        };
                        lastContainer.addEventListener(
                          "mouseover",
                          handleMouseOver,
                        );
                        lastContainer.addEventListener(
                          "mouseout",
                          handleMouseOut,
                        );

                        // Hide close button
                        const closeButton =
                          lastContainer.parentElement?.querySelector(
                            ".gm-ui-hover-effect",
                          );
                        if (closeButton) {
                          closeButton.style.display = "none";
                        }
                      }

                      // Attach mouse event listeners to InfoWindow tail/arrow element
                      const infoWindowTails =
                        document.querySelectorAll(".gm-style-iw-tc");
                      const lastTail =
                        infoWindowTails[infoWindowTails.length - 1];
                      if (lastTail) {
                        const handleMouseOver = () => {
                          if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                            closeTimeoutRef.current = null;
                          }
                        };
                        const handleMouseOut = () => {
                          closeTimeoutRef.current = setTimeout(() => {
                            setSelectedProjectId(null);
                            setInfoWindowPosition(null);
                          }, 300);
                        };
                        lastTail.addEventListener("mouseover", handleMouseOver);
                        lastTail.addEventListener("mouseout", handleMouseOut);
                      }
                    }, 0);
                  }}
                  onCloseClick={() => {
                    setSelectedProjectId(null);
                    setInfoWindowPosition(null);
                  }}
                >
                  <MapTooltip
                    mapInstance={mapInstance}
                    getProjectPosition={getProjectPosition}
                    selectedProject={selectedProject}
                    setImagePreview={setImagePreview}
                    translations={translations}
                    setSelectedProjectId={setSelectedProjectId}
                    user={user}
                    setDeletingProject={setDeletingProject}
                    setShowDeleteModal={setShowDeleteModal}
                  />
                </InfoWindow>
              )}
          </GoogleMap>
        )}
      </LoadScript>

      {imagePreview && (
        <div
          className="map-image-modal"
          onClick={() => setImagePreview(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Escape" || event.key === "Esc") {
              event.stopPropagation();
              setImagePreview(null);
            }
          }}
        >
          <div className="map-image-modal__backdrop" />
          <div className="map-image-modal__content">
            <img
              src={imagePreview}
              alt="Location"
              className="map-image-modal__image"
            />
          </div>
        </div>
      )}
    </>
  );
}
